import StateObserver from 'src/StateObserver';
import PopupCheats, { Item } from 'src/game/cheats/PopupCheats';
import { CheatData } from 'src/game/cheats/cheats.sequences';
import { closeAllPopups } from 'src/lib/popups/popupOpenClose';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';

/** Cheats menu category containing Dream Drift development controls. */
const MATCH2_DREAM_DRIFT = 'match2 Dream Drift';

/**
 * Alert when a Dream Drift cheat action fails.
 *
 * @param issue Backend issue returned by the requested cheat.
 * @returns Nothing.
 */
function showDreamDriftCheatIssue(issue: string | undefined): void {
  // Successful actions should not interrupt the cheat workflow.
  if (!issue) return;

  // Display every backend failure returned by the requested cheat.
  window.alert(issue);
}

/**
 * Build the Dream Drift cheats menu entries.
 *
 * @returns Cheats grouped under the Match-2 Dream Drift category.
 */
const list = () => {
  // Create the feature-specific cheat group.
  const cheats: Record<string, CheatData[]> = {};

  // Register the available Dream Drift developer operations.
  cheats[MATCH2_DREAM_DRIFT] = [
    {
      text: 'Reset Dream Drift',
      color: 'cancel',
      cheat: async () => {
        // Delete the backend state and close stale feature popups.
        await StateObserver.invoke.cheat_dreamDriftReset();
        closeAllPopups();
      },
    },
    {
      text: 'Advance Level',
      color: 'secondary',
      cheat: async () => {
        // Advance one node without interrupting successful cheat workflows.
        const result =
          await StateObserver.invoke.cheat_dreamDriftAdvanceLevel();
        showDreamDriftCheatIssue(result.issue);
      },
    },
    {
      text: 'Lose Level',
      color: 'secondary',
      cheat: async () => {
        // Lose one node without interrupting successful cheat workflows.
        const result = await StateObserver.invoke.cheat_dreamDriftLoseLevel();
        showDreamDriftCheatIssue(result.issue);
      },
    },
    {
      text: 'Expire Instance',
      color: 'secondary',
      cheat: async () => {
        // Expire the current instance through the production resolution path.
        const issue =
          await StateObserver.invoke.cheat_dreamDriftExpireInstance();
        showDreamDriftCheatIssue(issue);
        if (issue) return;

        // Close stale feature popups after the terminal state changes.
        closeAllPopups();
      },
    },
  ];

  // Return the completed feature cheat group.
  return cheats;
};

/**
 * Render the Dream Drift cheats as vertical buttons.
 *
 * @param args Cheats popup rendering context.
 * @returns Nothing.
 */
const ui = (args: {
  key: string;
  popup: PopupCheats;
  item: Item;
  title: LangBitmapFontTextView;
  setting: CheatData[];
  parent: Item | null;
}) => {
  // Ignore unrelated Match-2 cheat categories.
  if (args.key !== MATCH2_DREAM_DRIFT) return;

  // Center the category title above its vertical button list.
  args.title.updateOpts({
    width: args.item.style.width - 40,
    height: 20,
    y: 8,
    align: 'center',
  });
  args.popup.createVerticalButtons(args.item, args.setting);
};

/** Dream Drift list and renderer hooks consumed by the Match-2 cheats menu. */
export const match2DreamDriftCheats = {
  list,
  ui,
};
